import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

export const BIG_QUERY_SYNC_PREVIEW_STEPS = [
  { id: 1 as const, label: 'Status', shortLabel: '1', hint: 'Progresso, erros e bloqueios' },
  { id: 2 as const, label: 'Diff', shortLabel: '2', hint: 'Linhas alteradas (paginado)' },
  { id: 3 as const, label: 'Decisão', shortLabel: '3', hint: 'Aprovar ou rejeitar o job' },
] as const;

export type BigQuerySyncPreviewStepId = typeof BIG_QUERY_SYNC_PREVIEW_STEPS[number]['id'];

type BigQuerySyncPreviewStepperProps = {
  activeStep: BigQuerySyncPreviewStepId;
  onStepChange: (step: BigQuerySyncPreviewStepId) => void;
  className?: string;
};

export function BigQuerySyncPreviewStepper({
  activeStep,
  onStepChange,
  className,
}: BigQuerySyncPreviewStepperProps) {
  return (
    <nav className={cn('flex flex-col gap-2', className)} aria-label="Etapas da pré-visualização">
      <ol className="flex flex-wrap items-stretch gap-2">
        {BIG_QUERY_SYNC_PREVIEW_STEPS.map((step, index) => {
          const isActive = activeStep === step.id;
          const isPast = activeStep > step.id;
          const isLast = index === BIG_QUERY_SYNC_PREVIEW_STEPS.length - 1;

          return (
            <li key={step.id} className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={() => onStepChange(step.id)}
                title={`${step.label} — ${step.hint}`}
                aria-current={isActive ? 'step' : undefined}
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors sm:min-w-[7.75rem] sm:flex-none sm:px-3',
                  isActive &&
                    'border-primary bg-primary/10 font-medium text-foreground shadow-xs ring-1 ring-primary/20',
                  !isActive &&
                    isPast &&
                    'border-border bg-muted/40 text-muted-foreground hover:bg-muted/70',
                  !isActive &&
                    !isPast &&
                    'border-border bg-background text-muted-foreground hover:bg-muted/30'
                )}
              >
                <span
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                    isActive && 'border-primary bg-primary text-primary-foreground',
                    !isActive && isPast && 'border-muted-foreground/40 bg-muted text-foreground',
                    !isActive && !isPast && 'border-muted-foreground/30 bg-muted/50'
                  )}
                >
                  {isPast ? (
                    <Check className="size-3.5" aria-hidden />
                  ) : (
                    <span>{step.shortLabel}</span>
                  )}
                </span>
                <span className="min-w-0 truncate">
                  <span className="sm:hidden">{step.shortLabel}</span>
                  <span className="hidden sm:inline">{step.label}</span>
                </span>
              </button>
              {!isLast ? (
                <span className="hidden h-px w-5 shrink-0 bg-border sm:block sm:w-8" aria-hidden />
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground sm:pl-1">
        {BIG_QUERY_SYNC_PREVIEW_STEPS.find((s) => s.id === activeStep)?.hint}
      </p>
    </nav>
  );
}

export function goToAdjacentBigQuerySyncStep(
  current: BigQuerySyncPreviewStepId,
  direction: -1 | 1
): BigQuerySyncPreviewStepId {
  const next = current + direction;
  if (next < 1) return 1;
  if (next > 3) return 3;
  return next as BigQuerySyncPreviewStepId;
}
